import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Box } from '@mui/material';
import Input from 'components/V2/Input';
import PropTypes from 'prop-types';

import styles from './manageBookingChannels.module.css';

import { ReactComponent as SearchIcon } from 'asset/IconsPack/at_search-sm-filled.svg';

import { BOOKING_CHANNELS_COPY } from '../../constants';
import { STR_BOOKING_CHANNELS, TOTAL_BOOKING_CHANNELS } from '../../constants/bookingChannels';
import useBookingChannelSelection from '../../hooks/useBookingChannelSelection';
import useBookingChannels from '../../hooks/useBookingChannels';
import { filterChannelsBySearch } from '../../utils';
import ListingsModal from '../ListingsModal';
import NoResultFound from '../NoResultFound';
import ChannelList from './components/ChannelList';
import ChannelListSkeleton from './components/ChannelListSkeleton';
import ChannelSelectionInfo from './components/ChannelSelectionInfo';
import DiscardChangesDialog from './components/DiscardChangesDialog';

function ManageBookingChannels({ open, onClose, integrationId }) {
    const [search, setSearch] = useState('');

    const { disabledValues, isLoading, isSaving, save } = useBookingChannels(integrationId, open);

    // Every control is inert while the selection is loading or a save is in flight.
    const isBusy = isLoading || isSaving;

    const {
        isSelected,
        toggle,
        selectAll,
        clearAll,
        reset,
        hasChanges,
        selectedCount,
        selectedValues,
        allSelected,
        noneSelected,
    } = useBookingChannelSelection({ channels: STR_BOOKING_CHANNELS, disabledValues, open });

    const isSearchActive = search.trim().length > 0;

    const visibleChannels = useMemo(
        () => filterChannelsBySearch(STR_BOOKING_CHANNELS, search),
        [search]
    );
    const visibleValues = useMemo(
        () => visibleChannels.map(({ value }) => value),
        [visibleChannels]
    );

    const noVisibleChannels = visibleValues.length === 0;
    const selectAllDisabled = noVisibleChannels || allSelected(visibleValues);
    const clearAllDisabled = noVisibleChannels || noneSelected(visibleValues);

    const [discardDialogOpen, setDiscardDialogOpen] = useState(false);

    const headerRef = useRef(null);
    const [headerHeight, setHeaderHeight] = useState(0);

    useEffect(() => {
        const node = headerRef.current;
        if (!open || !node) return undefined;

        const observer = new ResizeObserver(() => setHeaderHeight(node.offsetHeight));
        observer.observe(node);

        return () => observer.disconnect();
    }, [open]);

    // Every real close path (discard, save success, cancel-with-no-changes) routes
    // through here, so resetting inline covers reopening without a stale search
    // term or a leftover discard-dialog state
    const handleClose = () => {
        setSearch('');
        setDiscardDialogOpen(false);
        onClose();
    };

    const requestClose = () => {
        if (isSaving) return;
        if (hasChanges) {
            setDiscardDialogOpen(true);
            return;
        }
        handleClose();
    };

    const handleKeepEditing = () => setDiscardDialogOpen(false);

    const handleDiscard = () => {
        setDiscardDialogOpen(false);
        reset();
        handleClose();
    };

    const handleSave = async () => {
        const saved = await save(selectedValues);
        if (saved) handleClose();
    };

    return (
        <>
            <ListingsModal open={open} onClose={requestClose} className={styles.container}>
                <ListingsModal.Header
                    title={BOOKING_CHANNELS_COPY.TITLE}
                    subTitle={BOOKING_CHANNELS_COPY.SUB_TITLE}
                    onClose={requestClose}
                    headerRef={headerRef}
                >
                    <Box className={styles.headerChildren}>
                        <Input
                            variant="outlined"
                            label=""
                            name="bookingChannelSearch"
                            value={search}
                            placeholder={BOOKING_CHANNELS_COPY.SEARCH_PLACEHOLDER}
                            startAdornment
                            overrideStyles={{ startAdornment: styles.searchIcon }}
                            StartAdornmentIcon={SearchIcon}
                            handleChange={(event) => setSearch(event.target.value)}
                            disabled={isBusy}
                            autoComplete="off"
                            className={styles.searchInput}
                        />
                        <ChannelSelectionInfo
                            selectedCount={selectedCount}
                            totalCount={TOTAL_BOOKING_CHANNELS}
                            shownCount={visibleValues.length}
                            isSearchActive={isSearchActive}
                            onSelectAll={() => selectAll(visibleValues)}
                            onClearAll={() => clearAll(visibleValues)}
                            selectAllDisabled={selectAllDisabled}
                            clearAllDisabled={clearAllDisabled}
                            disabled={isBusy}
                            isLoading={isLoading}
                        />
                    </Box>
                </ListingsModal.Header>
                <ListingsModal.Content>
                    <Box
                        className={styles.contentWrapper}
                        style={
                            headerHeight
                                ? { '--mbc-header-offset': `${headerHeight}px` }
                                : undefined
                        }
                    >
                        {isLoading && <ChannelListSkeleton />}
                        {!isLoading && noVisibleChannels && <NoResultFound />}
                        {!isLoading && !noVisibleChannels && (
                            <ChannelList
                                channels={visibleChannels}
                                isSelected={isSelected}
                                onToggle={toggle}
                                disabled={isSaving}
                            />
                        )}
                    </Box>
                </ListingsModal.Content>
                <ListingsModal.Footer
                    primaryBtnProps={{
                        title: BOOKING_CHANNELS_COPY.SAVE,
                        onClick: handleSave,
                        disabled: !hasChanges || isBusy,
                    }}
                    secondaryBtnProps={{
                        title: BOOKING_CHANNELS_COPY.CANCEL,
                        onClick: requestClose,
                        disabled: isSaving,
                    }}
                    showLoader={isSaving}
                />
            </ListingsModal>
            <DiscardChangesDialog
                open={discardDialogOpen}
                onKeepEditing={handleKeepEditing}
                onDiscard={handleDiscard}
            />
        </>
    );
}

ManageBookingChannels.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    integrationId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default ManageBookingChannels;
